import {signInWithEmailAndPassword} from "firebase/auth";
import {auth} from "../../../Components/MiddlePopup/SignInModal/firebaseConfig.ts";
import {updateToolCall} from "../../llm/contextManagement/toolCallUpdater.ts";
import {checkUserVersion} from "../../misc/userInsightUtils.ts";
import {formatSystemDetails} from "../../misc/envNameUtils.ts";
import {handleGiveInitialFiles, handleUpdateFileOpenStatus} from "./ide/fileHandlerUtils.ts";
import {handleGiveResources} from "./shared/resourcesHandlerUtils.ts";
import {handleGiveBadFileOperationResults} from "./ide/handleGiveBadFileOperationResults.ts";
import {handleGiveFileAlterResults} from "./ide/handleGiveFileAlterResults.ts";
import {handleGiveFileForCodeApply} from "./ide/handleGiveFileForCodeApply.ts";
import {handleGiveSearchCodebaseResults} from "./ide/handleGiveSearchCodebaseResults.ts";
import {handleGiveTerminalOutput} from "./ide/handleGiveTerminalOutput.ts";
import {handleGiveOpenFileResults} from "./ide/handleGiveOpenFileResults.ts";
import {handleGiveGrepResults} from "./ide/handleGiveGrepResults.ts";
import {handleGiveViewDirResults} from "./ide/handleGiveViewDirResults.ts";
import {handleGiveReviewChangesResults} from "./ide/handleGiveReviewChangesResults.ts";
import {handleTriggerVectorSearch} from "./ide/handleTriggerVectorSearch.ts";
import {getAppleProfileInfo} from "../shared/appleTokenUtils.ts";
import {getGoogleProfileInfo} from "../shared/googleTokenUtils.ts";
import {addFilesToTracker} from "../../db/vectorDb/addFilesToTrackers.ts";
import {getFilesToUpdate} from "./ide/embeddings/getFilesToUpdate.ts";
import {clearEmbeddingsTracker} from "./ide/embeddings/clearEmbeddingsTracker.ts";
import {updateEmbeddings} from "../../db/vectorDb/updateEmbeddings.ts";
import {queueAllBatches} from "./ide/embeddings/embeddingsQueue.ts";
import languageToIdMapping from "../../IdMappings/languageToIdMapping.ts";
import {startRecording} from "../../../Components/Bottom/MediaInput/utils/recordingUtils.ts";
import {getSpeechPhrases} from "../../../Components/Bottom/VoiceMode/VoiceMode.tsx";
import {truncateHighlyDynamicToolMessages, truncateOnRepeatToolMessages} from "../../llm/contextManagement/messageTrucater.ts";
import {addToolMessage} from "../../llm/responseStream/receiver/toolCallHandlers/toolCallFinish/utils/utils.ts";
import {runOrQueueToolCall} from "../../llm/responseStream/receiver/toolCallHandlers/toolCallFinish/handlers/appToolCallHandler/ide/ideToolCallHandler.ts";

const leaveWhenStopping = new Set(['giveFileAlterResults', 'giveBadFileOperationResults', 'giveCodebaseSearchResults', 'giveTerminalOutput', 'giveOpenFileResults', 'giveGrepResults', 'giveDirectoryView', 'giveReviewChangesResults']);

const IDE_RESPONSE_TIMEOUT_MS = 5000;

// Helper to clear an IDE response timeout
const clearIdeResponseTimeout = (miscMidRenderRefs: any, toolCallId: string) => {
    if (miscMidRenderRefs.current.ideResponseTimeouts[toolCallId]) {
        clearTimeout(miscMidRenderRefs.current.ideResponseTimeouts[toolCallId]);
        delete miscMidRenderRefs.current.ideResponseTimeouts[toolCallId];
    }
};

export const handleMessage = async (functionName: string, data: any, currentMessageRef: any, setMessages: any, messagesRef: any, messagesSequenceRef: any, setUserObj: any, setEnvironmentObj: any, setSettingsObj: any, setDynamicStateObj: any, updateElementInProgress: any, userObjRef: any, environmentObjRef: any, settingsObjRef: any, dynamicStateObjRef: any, websocketRef: any, setupWebsocketHelper: any, miscMidRenderRefs: any, llmRequestRefs: any, processSubmitHelper: any, updateOpenFileStatuses: any, updateProjectFileBatchIds: any, updateUnreviewedFileTracker: any, resetStateHelper: any, stopRecordingHelper: any) => {
    if (dynamicStateObjRef.current.isStopping && leaveWhenStopping.has(functionName)) return;

    // For functions that require responses from the IDE, exit when stopping
    if (leaveWhenStopping.has(functionName)) {
        if (dynamicStateObjRef.current.isStopping) return;
        // Also truncate alter file tool messages
        truncateHighlyDynamicToolMessages(llmRequestRefs.current.toolMessages);
        if (functionName === "giveReviewChangesResults") {
            truncateOnRepeatToolMessages(llmRequestRefs.current.toolMessages, "reviewChanges");
        }
    }

    switch (functionName) {
        case 'checkIfWebviewReady':
            if (environmentObjRef.current.environment.includes("jetbrains")) {
                window.jetbrainsPostMessage(JSON.stringify({functionName: "webviewReady", data: []}));
            }
            break;
        case 'giveApiKeys':
            const anthropicApiKey = data?.anthropic && data.anthropic.trim() !== '' ? data.anthropic : null;
            const xAiApiKey = data?.xai && data.xai.trim() !== '' ? data.xai : null;
            const openAiApiKey = data?.openai && data.openai.trim() !== '' ? data.openai : null;
            const geminiApiKey = data?.gemini && data.gemini.trim() !== '' ? data.gemini : null;

            setEnvironmentObj({ anthropicApiKey, xAiApiKey, openAiApiKey, geminiApiKey });
            break;

        case 'giveCurrentVersion':
            checkUserVersion(data, environmentObjRef.current.environment, environmentObjRef.current.vscode, setEnvironmentObj);
            break;
        case 'giveInitialFiles':
            handleGiveInitialFiles(data, setDynamicStateObj, environmentObjRef, userObjRef);
            break;
        case 'setFocusedFile':
            setDynamicStateObj({focusedFile: data});
            break;
        case 'updateFileOpenStatus':
            if (data[1] === "opened") addFilesToTracker(userObjRef, environmentObjRef, [data[0]]);
            handleUpdateFileOpenStatus(data, dynamicStateObjRef, setDynamicStateObj);
            break;
        case 'giveResources':
            await handleGiveResources(data, environmentObjRef, userObjRef, currentMessageRef, settingsObjRef, llmRequestRefs, processSubmitHelper);
            break;
        case 'giveFileForCodeApply':
            await handleGiveFileForCodeApply(data, setDynamicStateObj, environmentObjRef.current.environment, environmentObjRef.current.vscode);
            break;
        case 'giveFilesAndFolders':
            setDynamicStateObj({additionalIdeContext: data});
            break;
        case 'giveFileNames':
            updateProjectFileBatchIds(null, "clear");

            if (data.length >= 25000) {
                setDynamicStateObj({
                    embeddingInfo: {
                        status: "error",
                        error: `Max embeddings size is 2500 files. This project has at least ${data.length} files.`,
                        progress: 0
                    }
                });
                return;
            }
            setDynamicStateObj({
                embeddingInfo: {
                    status: "active",
                    error: null,
                    progress: 0
                }
            });

            let filesToProcess = data;
            if (environmentObjRef.current.projectEmbeddingsLastUpdated) {
                filesToProcess = await getFilesToUpdate(userObjRef.current.user, environmentObjRef.current.projectName, data);
            }

            if (filesToProcess.length === 0) {
                setDynamicStateObj({
                    embeddingInfo: {
                        status: "error",
                        error: "No files found that need their embeddings updated.",
                        progress: 0
                    }
                });
                return;
            }

            clearEmbeddingsTracker(userObjRef.current.user, environmentObjRef.current.projectName);

            // Queue all batches upfront
            const batches = queueAllBatches(filesToProcess, updateProjectFileBatchIds);
            miscMidRenderRefs.current.embeddingBatchQueue = [...batches];

            // Send all batches immediately
            for (const batch of batches) {
                if (environmentObjRef.current.environment.includes("jetbrains")) {
                    window.jetbrainsPostMessage(JSON.stringify({
                        functionName: "getFilesBatch",
                        data: [{ files: JSON.stringify(batch.files ?? []), batchId: batch.batchId }]
                    }));
                } else if (environmentObjRef.current.environment === "vscode") {
                    environmentObjRef.current.vscode.postMessage({
                        functionName: "getFilesBatch",
                        data: [{ files: batch.files ?? [], batchId: batch.batchId }]
                    });
                }
            }
            break;
        case 'giveFilesBatch':
            const fileContents = JSON.parse(data[0]);
            const batchId = data[1];

            // Don't add to projectFileBatchIds here since it was already added when queued
            await updateEmbeddings(userObjRef.current.user, environmentObjRef.current.projectName, fileContents, batchId, websocketRef, setupWebsocketHelper);
            break;
        case 'giveLineContentsToReplace':
            try {
                const deletedContents = JSON.parse(data[0]);
                const id = data[1];

                updateToolCall(llmRequestRefs, id, { deletedContents });

                setMessages((prev: any) => prev.deepCopy());

                // Start a 5 second timeout - if we don't receive giveFileAlterResults, continue anyway
                clearIdeResponseTimeout(miscMidRenderRefs, id);

                miscMidRenderRefs.current.ideResponseTimeouts[id] = setTimeout(async () => {
                    // Check if the tool call is still in the queue (meaning we haven't received the final response)
                    const stillPending = llmRequestRefs.current.toolCallQueue.some((tc: any) => tc.id === id);
                    if (stillPending && llmRequestRefs.current.toolCalls[id]) {
                        console.warn(`IDE response timeout for tool call ${id}, continuing with fallback`);

                        const toolCall = llmRequestRefs.current.toolCalls[id];
                        const { name, args } = toolCall;

                        // Add a tool message with a timeout error message
                        addToolMessage(
                            { id, name, args },
                            { errorMessage: "Edit went through, but results from the edit timed out." },
                            llmRequestRefs,
                            currentMessageRef,
                            messagesSequenceRef,
                            setMessages,
                            messagesRef
                        );

                        // Clean up and continue the queue
                        updateElementInProgress(id, 'delete');
                        llmRequestRefs.current.toolCallQueue = llmRequestRefs.current.toolCallQueue.filter((tc: any) => tc.id !== id);
                        await runOrQueueToolCall(llmRequestRefs, "trigger", environmentObjRef, null, null);
                    }

                    delete miscMidRenderRefs.current.ideResponseTimeouts[id];
                }, IDE_RESPONSE_TIMEOUT_MS);
            } catch (error) {
                console.error('Error in giveLineContentsToReplace:', error);
            }
            break;

        case 'giveFileAlterResults':
            // Clear any pending timeout for this tool call
            clearIdeResponseTimeout(miscMidRenderRefs, data[6]);
            await handleGiveFileAlterResults(data, llmRequestRefs, currentMessageRef, messagesSequenceRef, setMessages, messagesRef, updateElementInProgress, updateUnreviewedFileTracker, environmentObjRef, dynamicStateObjRef);
            break;
        case 'giveBadFileOperationResults':
            // Clear any pending timeout for this tool call
            clearIdeResponseTimeout(miscMidRenderRefs, data[1]);
            await handleGiveBadFileOperationResults(data, llmRequestRefs, websocketRef, currentMessageRef, messagesSequenceRef, setMessages, messagesRef, updateElementInProgress, environmentObjRef);
            break;
        case 'giveCodebaseSearchResults':
            await handleGiveSearchCodebaseResults(data, llmRequestRefs, websocketRef, currentMessageRef, messagesSequenceRef, setMessages, messagesRef, updateElementInProgress, environmentObjRef);
            break;
        case 'giveTerminalOutput':
            await handleGiveTerminalOutput(data, llmRequestRefs, currentMessageRef, messagesSequenceRef, setMessages, messagesRef, updateElementInProgress, environmentObjRef);
            break;
        case 'giveOpenFileResults':
            await handleGiveOpenFileResults(data, llmRequestRefs, websocketRef, currentMessageRef, messagesSequenceRef, setMessages, messagesRef, updateElementInProgress, environmentObjRef);
            break;
        case 'giveGrepResults':
            await handleGiveGrepResults(data, llmRequestRefs, websocketRef, currentMessageRef, messagesSequenceRef, setMessages, messagesRef, updateElementInProgress, environmentObjRef);
            break;
        case 'giveDirectoryView':
            await handleGiveViewDirResults(data, llmRequestRefs, websocketRef, currentMessageRef, messagesSequenceRef, setMessages, messagesRef, updateElementInProgress, environmentObjRef);
            break;
        case 'updateUnreviewedFileTracker':
            updateUnreviewedFileTracker(data, null, "delete");
            break;
        case 'setFileToSelected':
            updateOpenFileStatuses({ filePath: data, isSelected: true }, "toggle");
            break;
        case 'giveGoogleSignInToken':
            const token = data[0];
            const tokenType = data[1];

            const googleProfileInfo = await getGoogleProfileInfo(token, tokenType, environmentObjRef.current.environment, environmentObjRef.current.vscode);
            setUserObj({user: googleProfileInfo});

            setDynamicStateObj({visiblePopup: null});
            break;
        case 'giveAppleSignInToken':
            const appleIdentityToken = data[0];
            const appleUser = await getAppleProfileInfo(appleIdentityToken);
            setUserObj({user: appleUser});
            setDynamicStateObj({visiblePopup: null});
            break;
        case 'giveEmailPass':
            const email = data[0];
            const password = data[1];

            await signInWithEmailAndPassword(auth, email, password);
            break;
        case 'giveProjectName':
            setEnvironmentObj({projectName: data});
            break;
        case 'giveSystemDetails':
            const systemDetails = formatSystemDetails(data);
            setEnvironmentObj({systemDetails});
            break;
        case 'setupChrome':
            setEnvironmentObj({environment: 'chrome'});
            break;
        case 'changeTheme':
            if (settingsObjRef.current.autoTheme) setSettingsObj({theme: data});
            break;
        case 'clearChat':
            resetStateHelper();
            break;
        case 'triggerToggleRecording':
            if (dynamicStateObjRef.current.recordingType) {
                stopRecordingHelper();
            }
            else {
                await startRecording('speech', {}, miscMidRenderRefs, environmentObjRef.current.environment, environmentObjRef.current.vscode, setDynamicStateObj, websocketRef, setupWebsocketHelper);
            }
            break;
        case 'audioData':
            if (dynamicStateObjRef.current.recordingType === "speech") {
                if (websocketRef.current?.readyState === WebSocket.OPEN) {
                    const speechData = {
                        functionName: 'speechToText',
                        args: {
                            audioData: data, isBase64: true, inputType: 'pcm',
                            primaryLanguage: languageToIdMapping[settingsObjRef.current.primaryLanguage],
                            secondaryLanguage: settingsObjRef.current.secondaryLanguage? languageToIdMapping[settingsObjRef.current.secondaryLanguage] : null,
                            speechPhrases: getSpeechPhrases(settingsObjRef)
                        }
                    };
                    websocketRef.current.send(JSON.stringify(speechData));
                }
            } else if (dynamicStateObjRef.current.recordingType === "audio") {
                // For audio recording from mobile, send to server for processing
                if (websocketRef.current?.readyState === WebSocket.OPEN) {
                    try {
                        websocketRef.current.send(JSON.stringify({
                            functionName: 'audioRecording',
                            args: {
                                audioData: data,
                                isBase64: true,
                                inputType: 'pcm'
                            }
                        }));
                    } catch (error) {
                        console.error('Failed to send audio recording data:', error);
                    }
                }
            } else {
                miscMidRenderRefs.current.audioChunks.push(data);
            }
            break;

        case 'stopRecording':
            if (websocketRef.current?.readyState === WebSocket.OPEN) {
                websocketRef.current.send(JSON.stringify({
                    functionName: 'speechToText',
                    args: { done: true }
                }));
            }
            break;
        case 'appHandshake':
            const environment = data[0];

            if (environment === 'mobile') {
                window.ReactNativeWebView.postMessage(JSON.stringify({
                    functionName: "appHandshake",
                    data: []
                }));
            }
            break;
        case 'giveReviewChangesResults':
            await handleGiveReviewChangesResults(data, llmRequestRefs, currentMessageRef, messagesSequenceRef, setMessages, messagesRef, updateElementInProgress, environmentObjRef, dynamicStateObjRef);
            break;
        case 'triggerVectorSearch':
            await handleTriggerVectorSearch(data, environmentObjRef, userObjRef);
            break;
        case 'error':
            setDynamicStateObj({errorMessage: data.error});
            break;
    }
};
